/**
 * The only links a seller may put in a listing description or shop bio: YouTube, Vimeo and
 * SoundCloud, matched on the link's real host (a host that only mentions one of them, such
 * as evil.com/youtube.com or youtube.com.evil.net, is not one of them). Pure, so the
 * validators, the editor and the description's renderer share it.
 */

// Text that looks like a link: with a scheme or www., or a bare domain
export const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+(?:com|org|net|io|co|me|info|biz|tv|edu|gov|uk|de|fr|es|it|nl|be|ch|at|au|ca|us|ru|cn|jp|kr|in|br|mx|pl|se|no|fi|dk|ie|nz|za|sg|hk|my|ph|th|vn|id|tw|ar|cl|co|pe|ve|ec|uy|py|bo|cr|pa|do|pr|cu|gt|hn|sv|ni|pt|gr|cz|hu|ro|bg|hr|sk|si|lt|lv|ee|is|lu|mt|cy|al|ba|rs|me|mk|md|ua|by|kz|uz|az|ge|am|kg|tj|tm|mn|af|pk|bd|lk|np|mm|kh|la|vn|bn|tl|pg|fj|ws|to|vu|sb|fm|mh|pw|ki|nr|tv|wf|nu|tk|as|gu|mp|vi|pr|aw|cw|sx|bq|ai|vg|ky|bm|tc|ms|ag|dm|gd|kn|lc|vc|bb|tt|jm|ht|bs|sr|gy|gf|mq|gp|bl|mf|pm)(?:\/[^\s<>"']*)?/gi;

// Sites a link may point to (any of their subdomains too)
const LINK_SITES = ['youtube.com', 'youtu.be', 'vimeo.com', 'soundcloud.com'];

// Hosts a description's video or audio player may load from
const EMBED_HOSTS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'w.soundcloud.com',
];

/** The lower-case host a link points to, or null when it isn't a link. */
export const hostOf = link => {
  if (!link || typeof link !== 'string') return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(link) ? link : `https://${link}`;
  try {
    return new URL(withScheme).hostname.toLowerCase();
  } catch (e) {
    return null;
  }
};

const onSite = (host, site) => host === site || host.endsWith(`.${site}`);

/** Whether a link points to one of the allowed sites. */
export const isAllowedLink = link => {
  const host = hostOf(link);
  return !!host && LINK_SITES.some(site => onSite(host, site));
};

/** Whether a player (an iframe's src) loads from an allowed host, over https. */
export const isAllowedEmbedSrc = src => {
  if (!src || typeof src !== 'string' || !/^https:\/\//i.test(src)) return false;
  return EMBED_HOSTS.includes(hostOf(src));
};

/** Whether text holds a link to anywhere but the allowed sites. */
export const containsBlockedLink = text => {
  if (!text || typeof text !== 'string') return false;
  const links = text.match(URL_PATTERN) || [];
  return links.some(link => !isAllowedLink(link));
};
